// @ts-nocheck — legacy v1 migration step (converge scenes), kept only for the v1→v2 migration tooling.
import { applyAutoConvergeToPhoneReplies } from '../../systems/legacy-phone-branch';
import type { V1ContentItem as ContentItem, V1Scenario as Scenario, V1Scene as Scene } from '../../systems/legacy-v1-types';
import {
  convergeSceneId,
  ensureSceneBlockIds,
  getBranchingOptions,
  isBranchSceneId,
  isConvergeSceneId,
  isPhoneReplyItem,
  resyncChoiceBranches,
} from './inline-choice-model';
import { uniqueId } from './utils';

const BRANCHING_TYPES = new Set(['choice', 'phoneCall', 'phoneChat']);

function isNarrSetupSceneId(id: string): boolean {
  return id.endsWith('__narr');
}

function narrSetupSceneId(mainSceneId: string): string {
  return `${mainSceneId}__narr`;
}

function isMigratableMainSceneId(id: string): boolean {
  if (isConvergeSceneId(id)) return false;
  if (isBranchSceneId(id)) return false;
  if (isNarrSetupSceneId(id)) return false;
  return true;
}

function isBranchingItem(item: ContentItem): boolean {
  return BRANCHING_TYPES.has(item.type);
}

function firstBranchingIndex(content: ContentItem[]): number {
  return content.findIndex((item) => isBranchingItem(item));
}

function lastForwardGoto(content: ContentItem[]): string | undefined {
  for (let i = content.length - 1; i >= 0; i--) {
    const item = content[i]!;
    if (item.type === 'dialogue' || item.type === 'timedOverlay' || item.type === 'phoneCallBranchLine') {
      if (item.gotoScene) return item.gotoScene;
    }
    if ((item.type === 'phoneCall' || item.type === 'phoneChat') && !item.replyOptions?.length) {
      if (item.gotoScene) return item.gotoScene;
    }
  }
  return undefined;
}

function branchExitTarget(scenario: Scenario, sceneId: string, visited = new Set<string>()): string | undefined {
  if (visited.has(sceneId)) return undefined;
  visited.add(sceneId);
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return undefined;

  const direct = lastForwardGoto(scene.content);
  if (direct) return direct;

  for (let i = scene.content.length - 1; i >= 0; i--) {
    const item = scene.content[i];
    const options = getBranchingOptions(item);
    if (!options?.length) continue;
    const exits = options.map((opt) => {
      if (!opt.gotoScene) return undefined;
      const branchScene = scenario.scenes.find((s) => s.id === opt.gotoScene);
      if (branchScene) return branchExitTarget(scenario, opt.gotoScene, visited);
      return opt.gotoScene;
    });
    if (exits.some((t) => !t)) continue;
    const first = exits[0]!;
    if (exits.every((t) => t === first)) return first;
  }
  return undefined;
}

function redirectGotoSceneRefs(scenario: Scenario, from: string, to: string): Scenario {
  const patchItem = (item: ContentItem): ContentItem => {
    switch (item.type) {
      case 'dialogue':
      case 'timedOverlay':
      case 'phoneCallBranchLine':
        return item.gotoScene === from ? { ...item, gotoScene: to } : item;
      case 'phoneChat':
      case 'phoneCall': {
        let next = item.gotoScene === from ? { ...item, gotoScene: to } : item;
        if (next.replyOptions?.length) {
          next = {
            ...next,
            replyOptions: next.replyOptions.map((opt) => ({
              ...opt,
              gotoScene: opt.gotoScene === from ? to : opt.gotoScene,
              replyOptions: opt.replyOptions?.map((nested) => ({
                ...nested,
                gotoScene: nested.gotoScene === from ? to : nested.gotoScene,
              })),
            })),
          };
        }
        return next;
      }
      case 'choice':
        return {
          ...item,
          options: item.options.map((o) =>
            o.gotoScene === from ? { ...o, gotoScene: to } : o
          ),
        };
      default:
        return item;
    }
  };

  return {
    ...scenario,
    scenes: scenario.scenes.map((scene) => ({
      ...scene,
      content: scene.content.map(patchItem),
    })),
  };
}

function redirectMetadataEntryPoints(scenario: Scenario, from: string, to: string): Scenario {
  const m = scenario.metadata;
  let next = scenario;
  if (m.startScene === from) {
    next = { ...next, metadata: { ...m, startScene: to } };
  }
  if (m.chapters?.some((c) => c.startScene === from)) {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        chapters: m.chapters!.map((c) =>
          c.startScene === from ? { ...c, startScene: to } : c
        ),
      },
    };
  }
  if (m.missions?.some((mi) => mi.startScene === from)) {
    next = {
      ...next,
      metadata: {
        ...next.metadata,
        missions: m.missions!.map((mi) =>
          mi.startScene === from ? { ...mi, startScene: to } : mi
        ),
      },
    };
  }
  return next;
}

function insertSceneBefore(scenario: Scenario, beforeSceneId: string, scene: Scene): Scenario {
  const idx = scenario.scenes.findIndex((s) => s.id === beforeSceneId);
  if (idx < 0) return { ...scenario, scenes: [...scenario.scenes, scene] };
  const scenes = [...scenario.scenes];
  scenes.splice(idx, 0, scene);
  return { ...scenario, scenes };
}

function upsertScene(scenario: Scenario, scene: Scene): Scenario {
  const idx = scenario.scenes.findIndex((s) => s.id === scene.id);
  if (idx < 0) return { ...scenario, scenes: [...scenario.scenes, scene] };
  const scenes = [...scenario.scenes];
  scenes[idx] = scene;
  return { ...scenario, scenes };
}

function removeScene(scenario: Scenario, sceneId: string): Scenario {
  return { ...scenario, scenes: scenario.scenes.filter((s) => s.id !== sceneId) };
}

function withForwardGoto(item: ContentItem, target: string): ContentItem {
  if (item.type === 'dialogue' || item.type === 'timedOverlay') return { ...item, gotoScene: target };
  if ((item.type === 'phoneCall' || item.type === 'phoneChat') && !item.replyOptions?.length) {
    return { ...item, gotoScene: target };
  }
  return item;
}

function splitPrepNarratives(scenario: Scenario): Scenario {
  let next = scenario;
  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    const branchIdx = firstBranchingIndex(scene.content);
    if (branchIdx <= 0) continue;

    const narrId = narrSetupSceneId(scene.id);
    if (next.scenes.some((s) => s.id === narrId)) continue;

    // Découpe les ids en tranches disjointes (prep = début, rest = suite) pour éviter que
    // le narratif de préparation et la scène branchante partagent les mêmes `blockId`.
    const withSceneIds = ensureSceneBlockIds(scene);
    const prep = withSceneIds.content.slice(0, branchIdx);
    const rest = withSceneIds.content.slice(branchIdx);
    const prepIds = withSceneIds.inlineBlockIds!.slice(0, branchIdx);
    const restIds = withSceneIds.inlineBlockIds!.slice(branchIdx);

    const prepContent = [...prep];
    const lastPrep = prepContent[prepContent.length - 1]!;
    prepContent[prepContent.length - 1] = withForwardGoto(lastPrep, scene.id);

    const narrScene: Scene = {
      id: narrId,
      background: scene.background,
      elements: scene.elements,
      content: prepContent,
      inlineBlockIds: prepIds,
    };

    next = insertSceneBefore(next, scene.id, narrScene);
    next = upsertScene(next, {
      ...scene,
      content: rest,
      inlineBlockIds: restIds,
    });
    next = redirectGotoSceneRefs(next, scene.id, narrId);
    next = redirectMetadataEntryPoints(next, scene.id, narrId);
  }
  return next;
}

function migratePhoneFinScenes(scenario: Scenario): Scenario {
  let next = scenario;
  const toRemove = new Set<string>();

  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    const branchIdx = firstBranchingIndex(scene.content);
    if (branchIdx < 0) continue;
    const item = scene.content[branchIdx]!;
    if (item.type !== 'phoneCall' && item.type !== 'phoneChat') continue;
    if (item.replyOptions?.length) continue;
    if (!item.gotoScene || isConvergeSceneId(item.gotoScene) || isBranchSceneId(item.gotoScene)) {
      continue;
    }

    const finScene = next.scenes.find((s) => s.id === item.gotoScene);
    if (!finScene || finScene.content.length === 0) continue;
    if (firstBranchingIndex(finScene.content) >= 0) continue;

    const convId = convergeSceneId(scene.id);
    const onward = lastForwardGoto(finScene.content);
    const finWithIds = ensureSceneBlockIds(finScene);
    const convContent = finWithIds.content.map((c, i) =>
      i === finWithIds.content.length - 1 && onward ? withForwardGoto(c, onward) : c
    );
    const convScene: Scene = {
      id: convId,
      background: scene.background,
      elements: scene.elements,
      content: convContent,
      inlineBlockIds: syncIds(finWithIds.inlineBlockIds!, convContent.length),
    };

    next = upsertScene(next, convScene);
    const updatedScene = next.scenes.find((s) => s.id === scene.id)!;
    next = upsertScene(next, {
      ...updatedScene,
      content: updatedScene.content.map((c, i) =>
        i === branchIdx && (c.type === 'phoneCall' || c.type === 'phoneChat')
          ? withForwardGoto(c, convId)
          : c
      ),
    });
    next = redirectGotoSceneRefs(next, finScene.id, convId);
    toRemove.add(finScene.id);
  }

  for (const id of toRemove) {
    if (next.scenes.some((s) => s.id === id)) next = removeScene(next, id);
  }
  return next;
}

function syncIds(ids: string[], len: number): string[] {
  const out = [...ids];
  while (out.length < len) out.push(uniqueId('blk'));
  out.length = len;
  return out;
}

function splitPrefixSuffix(content: ContentItem[]): { prefix: ContentItem[]; suffix: ContentItem[] } {
  const idx = firstBranchingIndex(content);
  if (idx < 0) return { prefix: content, suffix: [] };
  if (idx === 0) return { prefix: [], suffix: content };
  return { prefix: content.slice(0, idx), suffix: content.slice(idx) };
}

function patchSceneTreeGotoRefs(
  scenario: Scenario,
  rootSceneIds: string[],
  from: string,
  to: string
): Scenario {
  let next = scenario;
  const visited = new Set<string>();

  const visit = (sceneId: string) => {
    if (visited.has(sceneId)) return;
    visited.add(sceneId);
    const scene = next.scenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const content = scene.content.map((item) => {
      if (item.type === 'dialogue' && item.gotoScene === from) return { ...item, gotoScene: to };
      if (item.type === 'phoneCallBranchLine' && item.gotoScene === from) {
        return { ...item, gotoScene: to };
      }
      return item;
    });
    next = upsertScene(next, { ...scene, content });

    for (const item of scene.content) {
      const options = getBranchingOptions(item);
      options?.forEach((opt) => {
        if (opt.gotoScene && !isConvergeSceneId(opt.gotoScene)) visit(opt.gotoScene);
      });
    }
  };

  rootSceneIds.forEach(visit);
  return next;
}

function collectChoiceBranchSceneIds(scenario: Scenario, choiceScene: Scene, choiceIndex: number): string[] {
  const item = choiceScene.content[choiceIndex]!;
  const options = getBranchingOptions(item);
  if (!options) return [];
  const ids: string[] = [];
  for (const opt of options) {
    if (!opt.gotoScene) continue;
    if (isBranchSceneId(opt.gotoScene) && !isConvergeSceneId(opt.gotoScene)) {
      ids.push(opt.gotoScene);
      continue;
    }
    const ext = scenario.scenes.find((s) => s.id === opt.gotoScene);
    if (ext && isMigratableMainSceneId(ext.id)) ids.push(ext.id);
  }
  return ids;
}

function collectFlatChoiceSharedTarget(
  scenario: Scenario,
  choiceScene: Scene,
  choiceIndex: number
): string | undefined {
  const item = choiceScene.content[choiceIndex]!;
  if (item.type !== 'choice') return undefined;
  const targets = item.options.map((o) => o.gotoScene).filter(Boolean) as string[];
  if (targets.length === 0) return undefined;
  const first = targets[0]!;
  if (!targets.every((t) => t === first)) return undefined;
  if (isConvergeSceneId(first) || isBranchSceneId(first)) return undefined;
  const target = scenario.scenes.find((s) => s.id === first);
  if (!target || firstBranchingIndex(target.content) >= 0) return undefined;
  return first;
}

function migrateSingleOptionConverge(scenario: Scenario): Scenario {
  let next = scenario;
  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    scene.content.forEach((item, choiceIndex) => {
      if (item.type !== 'choice' || item.options.length !== 1) return;
      const opt = item.options[0]!;
      if (!opt.gotoScene || opt.gotoScene === convergeSceneId(scene.id)) return;
      if (isConvergeSceneId(opt.gotoScene) || isBranchSceneId(opt.gotoScene)) return;
      const target = next.scenes.find((s) => s.id === opt.gotoScene);
      if (!target || firstBranchingIndex(target.content) >= 0) return;

      const convId = convergeSceneId(scene.id);
      if (next.scenes.some((s) => s.id === convId)) return;

      const onward = lastForwardGoto(target.content);
      const finWithIds = ensureSceneBlockIds(target);
      const convContent = finWithIds.content.map((c, i) =>
        i === finWithIds.content.length - 1 && onward ? withForwardGoto(c, onward) : c
      );
      next = upsertScene(next, {
        id: convId,
        background: scene.background,
        elements: scene.elements,
        content: convContent,
        inlineBlockIds: syncIds(finWithIds.inlineBlockIds!, convContent.length),
      });
      const refreshed = next.scenes.find((s) => s.id === scene.id)!;
      next = upsertScene(next, {
        ...refreshed,
        content: refreshed.content.map((c, i) =>
          i === choiceIndex && c.type === 'choice'
            ? { ...c, options: [{ ...opt, gotoScene: convId }] }
            : c
        ),
      });
      next = redirectGotoSceneRefs(next, target.id, convId);
      next = removeScene(next, target.id);
    });
  }
  return next;
}

function migratePartialFlatChoiceFin(scenario: Scenario): Scenario {
  let next = scenario;
  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    scene.content.forEach((item, choiceIndex) => {
      if (item.type !== 'choice') return;
      const optionTargets = item.options
        .map((o) => o.gotoScene)
        .filter((id): id is string => Boolean(id));
      const narrEntryId = narrSetupSceneId(scene.id);
      const loopBackTargets = new Set(
        optionTargets.filter((tid) => {
          const exit = branchExitTarget(next, tid);
          return exit === scene.id || exit === narrEntryId;
        })
      );
      const finTargets = optionTargets.filter(
        (id) =>
          !loopBackTargets.has(id) &&
          !isConvergeSceneId(id) &&
          !isBranchSceneId(id) &&
          Boolean(next.scenes.find((s) => s.id === id)) &&
          firstBranchingIndex(next.scenes.find((s) => s.id === id)!.content) < 0
      );
      const uniqueFin = [...new Set(finTargets)];
      if (uniqueFin.length !== 1) return;
      const targetId = uniqueFin[0]!;
      if (item.options.every((o) => o.gotoScene === targetId || !o.gotoScene)) return;
      // Au moins une option diverge vers une branche : on ne migre que les options « fin ».
      const convId = convergeSceneId(scene.id);
      const target = next.scenes.find((s) => s.id === targetId)!;
      const onward = lastForwardGoto(target.content);
      const finWithIds = ensureSceneBlockIds(target);
      const convContent = finWithIds.content.map((c, i) =>
        i === finWithIds.content.length - 1 && onward ? withForwardGoto(c, onward) : c
      );
      next = upsertScene(next, {
        id: convId,
        background: scene.background,
        elements: scene.elements,
        content: convContent,
        inlineBlockIds: syncIds(finWithIds.inlineBlockIds!, convContent.length),
      });
      const refreshed = next.scenes.find((s) => s.id === scene.id)!;
      next = upsertScene(next, {
        ...refreshed,
        content: refreshed.content.map((c, i) =>
          i === choiceIndex && c.type === 'choice'
            ? {
                ...c,
                options: c.options.map((o) =>
                  o.gotoScene === targetId ? { ...o, gotoScene: convId } : o
                ),
              }
            : c
        ),
      });
      next = redirectGotoSceneRefs(next, targetId, convId);
      next = removeScene(next, targetId);
    });
  }
  return next;
}

function repairMissingConvergeScenes(scenario: Scenario): Scenario {
  let next = scenario;
  const needed = new Set<string>();
  const walk = (obj: unknown) => {
    if (!obj || typeof obj !== 'object') return;
    const rec = obj as Record<string, unknown>;
    if (typeof rec.gotoScene === 'string' && isConvergeSceneId(rec.gotoScene)) {
      needed.add(rec.gotoScene);
    }
    for (const v of Object.values(rec)) {
      if (Array.isArray(v)) v.forEach(walk);
      else if (v && typeof v === 'object') walk(v);
    }
  };
  next.scenes.forEach((s) => s.content.forEach(walk));
  for (const convId of needed) {
    if (next.scenes.some((s) => s.id === convId)) continue;
    const mainSceneId = convId.replace(/--converge$/, '');
    const main = next.scenes.find((s) => s.id === mainSceneId);
    if (!main) continue;
    next = upsertScene(next, {
      id: convId,
      background: main.background,
      elements: main.elements,
      content: [],
      inlineBlockIds: [],
    });
  }
  return next;
}

function migrateFlatChoiceFinScenes(scenario: Scenario): Scenario {
  let next = scenario;
  const toRemove = new Set<string>();

  const flatChoices: Array<{ sceneId: string; choiceIndex: number; targetId: string }> = [];
  for (const scene of next.scenes) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    scene.content.forEach((item, choiceIndex) => {
      if (item.type !== 'choice') return;
      const targetId = collectFlatChoiceSharedTarget(next, scene, choiceIndex);
      if (targetId) flatChoices.push({ sceneId: scene.id, choiceIndex, targetId });
    });
  }

  for (const { sceneId, choiceIndex, targetId } of flatChoices) {
    const scene = next.scenes.find((s) => s.id === sceneId);
    const target = next.scenes.find((s) => s.id === targetId);
    if (!scene || !target || target.content.length === 0) continue;

    const convId = convergeSceneId(sceneId);
    const onward = lastForwardGoto(target.content);
    const finWithIds = ensureSceneBlockIds(target);
    const convContent = finWithIds.content.map((c, i) =>
      i === finWithIds.content.length - 1 && onward ? withForwardGoto(c, onward) : c
    );
    next = upsertScene(next, {
      id: convId,
      background: scene.background,
      elements: scene.elements,
      content: convContent,
      inlineBlockIds: syncIds(finWithIds.inlineBlockIds!, convContent.length),
    });
    const refreshed = next.scenes.find((s) => s.id === sceneId)!;
    next = upsertScene(next, {
      ...refreshed,
      content: refreshed.content.map((c, i) =>
        i === choiceIndex && c.type === 'choice'
          ? { ...c, options: c.options.map((o) => ({ ...o, gotoScene: convId })) }
          : c
      ),
    });
    next = redirectGotoSceneRefs(next, targetId, convId);
    toRemove.add(targetId);
  }

  for (const id of toRemove) {
    if (next.scenes.some((s) => s.id === id)) next = removeScene(next, id);
  }
  return next;
}

function migrateChoiceSharedConvergences(scenario: Scenario): Scenario {
  let next = scenario;
  const strippedTargets = new Set<string>();

  const choiceScenes: Array<{ sceneId: string; choiceIndex: number }> = [];
  for (const scene of next.scenes) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    scene.content.forEach((item, i) => {
      if (item.type === 'choice') choiceScenes.push({ sceneId: scene.id, choiceIndex: i });
    });
  }

  const targetToChoices = new Map<string, string[]>();
  for (const { sceneId, choiceIndex } of choiceScenes) {
    const scene = next.scenes.find((s) => s.id === sceneId)!;
    const branchIds = collectChoiceBranchSceneIds(next, scene, choiceIndex);
    if (branchIds.length === 0) continue;
    const exits = branchIds.map((id) => branchExitTarget(next, id)).filter(Boolean);
    if (exits.length !== branchIds.length) continue;
    const first = exits[0]!;
    if (!exits.every((t) => t === first)) continue;
    if (isConvergeSceneId(first) || isBranchSceneId(first)) continue;
    const list = targetToChoices.get(first) ?? [];
    list.push(sceneId);
    targetToChoices.set(first, list);
  }

  for (const [targetId, choiceSceneIds] of targetToChoices) {
    const target = next.scenes.find((s) => s.id === targetId);
    if (!target) continue;
    const { prefix, suffix } = splitPrefixSuffix(target.content);
    if (prefix.length === 0) continue;

    const targetWithIds = ensureSceneBlockIds(target);
    const prefixIds = targetWithIds.inlineBlockIds!.slice(0, prefix.length);

      for (const choiceSceneId of [...new Set(choiceSceneIds)]) {
      const convId = convergeSceneId(choiceSceneId);
      const convContent = prefix.map((item, i) =>
        i === prefix.length - 1 ? withForwardGoto(item, targetId) : { ...item, gotoScene: undefined }
      );
      const choiceScene = next.scenes.find((s) => s.id === choiceSceneId)!;
      const convScene: Scene = {
        id: convId,
        background: choiceScene.background,
        elements: choiceScene.elements,
        content: convContent,
        inlineBlockIds: syncIds(prefixIds, convContent.length),
      };
      next = upsertScene(next, convScene);

      for (const { sceneId, choiceIndex } of choiceScenes.filter((c) => c.sceneId === choiceSceneId)) {
        const scene = next.scenes.find((s) => s.id === sceneId)!;
        const branchIds = collectChoiceBranchSceneIds(next, scene, choiceIndex);
        next = patchSceneTreeGotoRefs(next, branchIds, targetId, convId);
      }
    }

    if (!strippedTargets.has(targetId)) {
      const suffixIds = targetWithIds.inlineBlockIds!.slice(prefix.length);
      next = upsertScene(next, {
        ...target,
        content: suffix,
        inlineBlockIds: syncIds(suffixIds, suffix.length),
      });
      strippedTargets.add(targetId);
    }
  }

  return next;
}

function normalizePhoneReplyConvergence(scenario: Scenario): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      if (!isMigratableMainSceneId(scene.id)) return scene;
      const content = scene.content.map((item) => {
        if (!isPhoneReplyItem(item) || !item.replyOptions?.length) return item;
        const convId = convergeSceneId(scene.id);
        return {
          ...item,
          replyOptions: applyAutoConvergeToPhoneReplies(item.replyOptions, convId),
        };
      });
      return { ...scene, content };
    }),
  };
}

function resyncAllMainScenes(scenario: Scenario): Scenario {
  let next = scenario;
  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    if (scene.content.some((item) => isBranchingItem(item))) {
      next = resyncChoiceBranches(next, scene.id);
    }
  }
  return next;
}

function isolateBranchingScenes(scenario: Scenario): Scenario {
  let next = scenario;
  for (const scene of [...next.scenes]) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    const indices = scene.content
      .map((item, i) => (isBranchingItem(item) ? i : -1))
      .filter((i) => i >= 0);
    if (indices.length !== 1) continue;
    const idx = indices[0]!;
    if (idx === 0 && scene.content.length === 1) continue;
    if (idx !== 0) continue;
    if (scene.content.length === 1) continue;
    next = stripTrailingAfterBranch(next, scene.id, idx);
  }
  return next;
}

function stripTrailingAfterBranch(scenario: Scenario, sceneId: string, branchIdx: number): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;
  const trailing = scene.content.slice(branchIdx + 1);
  if (trailing.length === 0) return scenario;
  return resyncChoiceBranches(scenario, sceneId);
}

/**
 * Garantit l'unicité globale des `inlineBlockIds`. Les découpes de scènes (`__narr`) et les
 * copies de convergence peuvent réutiliser les ids de la scène source ; deux blocs de scènes
 * différentes se retrouvent alors avec le même `blockId`, ce qui casse la résolution de blocs
 * de l'éditeur inline (`buildChapterFlatBlocks` déduplique par id, `findBlockById` devient
 * ambigu). On conserve la PREMIÈRE occurrence (ordre du tableau `scenes`, comme `findBlockById`)
 * pour préserver les liens `gotoBlockId`, et on réattribue un id neuf aux occurrences suivantes.
 */
function dedupeBlockIds(scenario: Scenario): Scenario {
  const seen = new Set<string>();
  return {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      const withIds = ensureSceneBlockIds(scene);
      let changed = false;
      const inlineBlockIds = withIds.inlineBlockIds!.map((id) => {
        if (!seen.has(id)) {
          seen.add(id);
          return id;
        }
        let fresh = uniqueId('blk');
        while (seen.has(fresh)) fresh = uniqueId('blk');
        seen.add(fresh);
        changed = true;
        return fresh;
      });
      return changed ? { ...withIds, inlineBlockIds } : withIds;
    }),
  };
}

/** Migre un scénario hand-authored vers le modèle `--converge` de l'éditeur inline. */
export function migrateScenarioToConverge(scenario: Scenario): Scenario {
  let next = structuredClone(scenario);
  next = splitPrepNarratives(next);
  next = migratePhoneFinScenes(next);
  next = migrateChoiceSharedConvergences(next);
  next = migrateFlatChoiceFinScenes(next);
  next = migrateSingleOptionConverge(next);
  next = migratePartialFlatChoiceFin(next);
  next = normalizePhoneReplyConvergence(next);
  next = resyncAllMainScenes(next);
  next = isolateBranchingScenes(next);
  next = resyncAllMainScenes(next);
  next = repairMissingConvergeScenes(next);
  next = dedupeBlockIds(next);
  return next;
}

export function auditConvergeMigration(scenario: Scenario): string[] {
  const issues: string[] = [];
  for (const scene of scenario.scenes) {
    if (!isMigratableMainSceneId(scene.id)) continue;
    for (let i = 0; i < scene.content.length; i++) {
      const item = scene.content[i]!;
      if (!isBranchingItem(item)) continue;
      const after = scene.content.slice(i + 1).filter((c) => !isBranchingItem(c));
      if (after.length > 0) {
        issues.push(`${scene.id}: trailing content after ${item.type}`);
      }
      if (i > 0) {
        issues.push(`${scene.id}: prep content before ${item.type} (expected __narr split)`);
      }
      const convId = convergeSceneId(scene.id);
      const conv = scenario.scenes.find((s) => s.id === convId);
      if (item.type === 'phoneCall' || item.type === 'phoneChat') {
        if (item.replyOptions?.length) {
          const refsConv = JSON.stringify(item.replyOptions).includes(convId);
          if (!refsConv && !conv?.content.length) {
            issues.push(`${scene.id}: phone with replies but no ${convId}`);
          }
        } else if (item.gotoScene && !isConvergeSceneId(item.gotoScene) && !conv) {
          const target = scenario.scenes.find((s) => s.id === item.gotoScene);
          if (target && firstBranchingIndex(target.content) < 0) {
            issues.push(`${scene.id}: reply-less phone should use ${convId}, not ${item.gotoScene}`);
          }
        }
      }
    }
  }
  return issues;
}
